import { DATA_ENCRYPTION_KEY } from '../constants';

const NONCE_LENGTH = 12; // 96-bit nonce
const TAG_LENGTH_BITS = 128; // 128-bit auth tag

let cachedCryptoKeyPromise: Promise<CryptoKey> | null = null;

const getSubtleCrypto = (): SubtleCrypto => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle;
  }
  if (typeof globalThis !== 'undefined' && (globalThis as any).crypto && (globalThis as any).crypto.subtle) {
    return (globalThis as any).crypto.subtle;
  }
  try {
    // Dynamic check to prevent Webpack 5 browser bundle from resolving Node 'crypto'
    const req = typeof module !== 'undefined' && (module as any).require ? (module as any).require : null;
    if (req) {
      const cryptoModule = req('crypto');
      if (cryptoModule?.webcrypto?.subtle) {
        return cryptoModule.webcrypto.subtle;
      }
    }
  } catch {
    // Browser environment
  }

  throw new Error('Web Cryptography API (crypto.subtle) is not supported in this environment.');
};

const getRandomValues = (array: Uint8Array): Uint8Array => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    return window.crypto.getRandomValues(array);
  }
  if (typeof globalThis !== 'undefined' && (globalThis as any).crypto && (globalThis as any).crypto.getRandomValues) {
    return (globalThis as any).crypto.getRandomValues(array);
  }
  try {
    const req = typeof module !== 'undefined' && (module as any).require ? (module as any).require : null;
    if (req) {
      const cryptoModule = req('crypto');
      if (cryptoModule?.webcrypto?.getRandomValues) {
        return cryptoModule.webcrypto.getRandomValues(array);
      }
      if (cryptoModule?.randomFillSync) {
        return cryptoModule.randomFillSync(array);
      }
    }
  } catch {
    // Browser environment
  }
  throw new Error('Crypto getRandomValues is not supported in this environment.');
};

const getCryptoKey = async (): Promise<CryptoKey> => {
  if (!cachedCryptoKeyPromise) {
    cachedCryptoKeyPromise = (async () => {
      const subtle = getSubtleCrypto();
      const rawKeyBytes = new TextEncoder().encode(DATA_ENCRYPTION_KEY);

      let keyBytes: Uint8Array;
      if (rawKeyBytes.byteLength === 32) {
        keyBytes = rawKeyBytes;
      } else {
        // Hash key with SHA-256 if not exactly 32 bytes (matches backend .NET logic)
        const hash = await subtle.digest('SHA-256', rawKeyBytes);
        keyBytes = new Uint8Array(hash);
      }

      return subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['decrypt', 'encrypt']
      );
    })();
  }
  return cachedCryptoKeyPromise;
};

/**
 * Decrypts an AES-256-GCM encrypted ArrayBuffer payload.
 * Expected structure from .NET backend: [12-byte Nonce/IV] + [Ciphertext] + [16-byte GCM Tag].
 * Notice: W3C Web Crypto API subtle.decrypt expects `ciphertext` concatenated with the 16-byte tag,
 * which matches bytes [12..end] identically without extra splitting!
 */
export const decryptData = async (buffer: ArrayBuffer): Promise<string> => {
  if (!buffer || buffer.byteLength < NONCE_LENGTH + 16) {
    throw new Error('Invalid encrypted buffer length.');
  }

  const subtle = getSubtleCrypto();
  const key = await getCryptoKey();

  const iv = new Uint8Array(buffer, 0, NONCE_LENGTH);
  const ciphertextAndTag = new Uint8Array(buffer, NONCE_LENGTH);

  const decryptedBuffer = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: TAG_LENGTH_BITS,
    },
    key,
    ciphertextAndTag
  );

  return new TextDecoder('utf-8').decode(decryptedBuffer);
};

/**
 * Convenience helper to decrypt and JSON.parse in a single call.
 */
export const decryptJsonResponse = async <T>(buffer: ArrayBuffer): Promise<T> => {
  const jsonText = await decryptData(buffer);
  return JSON.parse(jsonText) as T;
};

/**
 * Encrypts a string into an AES-256-GCM encrypted Uint8Array.
 * Result layout: [12-byte Nonce/IV] + [Ciphertext] + [16-byte Tag]
 */
export const encryptData = async (plainText: string): Promise<Uint8Array> => {
  const subtle = getSubtleCrypto();
  const key = await getCryptoKey();

  const iv = new Uint8Array(NONCE_LENGTH);
  getRandomValues(iv);

  const encodedData = new TextEncoder().encode(plainText);
  const encryptedBuffer = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: TAG_LENGTH_BITS,
    },
    key,
    encodedData
  );

  const result = new Uint8Array(NONCE_LENGTH + encryptedBuffer.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(encryptedBuffer), NONCE_LENGTH);
  return result;
};
